(() => {
  if (window.paweverMessageCardBound) return;
  window.paweverMessageCardBound = true;

  let editor, draft, opener, saving = false, needsReview = false;
  const find = (selector) => editor.querySelector(selector);
  const cartUrl = (path) => (window.Shopify?.routes?.root || '/') + (path === 'get' ? 'cart.js' : 'cart/' + path);
  const parseProperties = (value) => {
    try {
      const properties = JSON.parse(value || '{}') || {};
      // Liquid serializes line properties as pairs; the Ajax API returns an object.
      return Array.isArray(properties) ? Object.fromEntries(properties) : properties;
    } catch (_) { return {}; }
  };

  async function request(path, body) {
    const response = await fetch(cartUrl(path), {
      method: body ? 'POST' : 'GET',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      ...(body ? { body: JSON.stringify(body) } : { cache: 'no-store' })
    });
    const data = await response.json();
    if (!response.ok) throw new Error(data.description || data.message || 'Unable to update your card.');
    return data;
  }

  function fitText(element, fraction) {
    let size = find('[data-pmc-preview]').clientWidth * fraction;
    element.style.fontSize = size + 'px';
    while (element.scrollHeight > element.clientHeight + 1 && size > 10) {
      size -= .5;
      element.style.fontSize = size + 'px';
    }
    return element.scrollHeight <= element.clientHeight + 1;
  }

  function updatePreview() {
    if (!editor?.open) return;
    const message = find('[data-pmc-message]').value;
    const signature = find('[data-pmc-signature]').value;
    const previewMessage = find('[data-pmc-preview-message]');
    const previewSignature = find('[data-pmc-preview-signature]');
    previewMessage.textContent = message;
    previewSignature.textContent = signature;
    const messageFits = fitText(previewMessage, .044);
    const signatureFits = fitText(previewSignature, .04);
    const fits = messageFits && signatureFits;
    find('[data-pmc-fit-warning]').hidden = fits;
    find('[data-pmc-message]').setCustomValidity(fits ? '' : 'Please shorten the text or use fewer line breaks.');
    find('[data-pmc-counter]').textContent = `${150 - message.length} characters left · 150 maximum`;
    find('[data-pmc-preview]').setAttribute('aria-label', `${draft.title} card preview. ${message} ${signature}`);
    editor.querySelectorAll('input[name="pmc-preset"]').forEach((radio) => { radio.checked = radio.value === message; });
  }

  function openEditor(button) {
    const root = document.querySelector('[data-pawever-gift]');
    if (saving || window.paweverCartServiceBusy || root?.classList.contains('is-loading')) return;
    editor = document.querySelector('[data-pmc-editor]');
    if (!editor) return;
    const variantId = button.dataset.variantId;
    const template = Array.from(editor.querySelectorAll('[data-pmc-variant]')).find((item) => item.dataset.pmcVariant === variantId);
    if (!template) return;
    const saved = button.matches('[data-pmc-edit]') ? button : root;
    const properties = parseProperties(saved?.dataset.messageProperties);
    draft = {
      variantId, title: template.dataset.title, style: template.dataset.style,
      lineKey: saved?.dataset.messageLineKey || '',
      properties
    };
    opener = button;
    needsReview = false;
    editor.querySelectorAll('button, input, textarea').forEach((control) => { control.disabled = false; });
    find('[data-pmc-save]').textContent = 'Save Message';
    find('[data-pmc-error]').hidden = true;
    find('[data-pmc-design-title]').textContent = draft.title;
    find('[data-pmc-price]').textContent = template.dataset.price;
    find('[data-pmc-preview-image]').src = template.content.querySelector('[data-pmc-art]').src;
    find('[data-pmc-presets]').replaceChildren(template.content.querySelector('[data-pmc-preset-options]').cloneNode(true));
    find('[data-pmc-message]').value = properties.Message || '';
    find('[data-pmc-signature]').value = properties.Signature || '';
    find('[data-pmc-save-note]').textContent = draft.lineKey
      ? 'Saving updates your existing card. It does not add another card.'
      : 'Your card is added only when you save your message.';
    editor.showModal();
    document.documentElement.classList.add('pmc-editor-open');
    editor.scrollTop = 0;
    editor.querySelector('h2').focus({ preventScroll: true });
    updatePreview();
  }

  function closeEditor() {
    if (saving) return;
    if (needsReview) { window.location.reload(); return; }
    editor.close();
    document.documentElement.classList.remove('pmc-editor-open');
    if (opener?.isConnected) opener.focus({ preventScroll: true });
  }

  async function saveMessage(event) {
    event.preventDefault();
    if (saving || needsReview) return;
    const message = find('[data-pmc-message]');
    message.value = message.value.trim();
    updatePreview();
    if (!find('[data-pmc-form]').reportValidity()) return;
    saving = true;
    editor.setAttribute('aria-busy', 'true');
    editor.querySelectorAll('button, input, textarea').forEach((control) => { control.disabled = true; });
    find('[data-pmc-save]').textContent = 'Saving…';
    find('[data-pmc-error]').hidden = true;
    let mutationStarted = false;
    try {
      // Read fresh keys/quantities before editing; a cart may have changed in another tab.
      const cart = await request('get');
      const oldItem = cart.items.find((item) => item.key === draft.lineKey);
      const cardIds = Array.from(editor.querySelectorAll('[data-pmc-variant]'), (item) => item.dataset.pmcVariant);
      const vipId = document.querySelector('[data-pawever-gift]')?.dataset.vipVariantId;
      const insuranceProductId = document.querySelector('[data-pawever-insurance]')?.dataset.productId;
      const hasOtherCard = !draft.lineKey && cart.items.some((item) => cardIds.includes(String(item.variant_id)));
      const hasMainItem = cart.items.some((item) => !cardIds.includes(String(item.variant_id)) && String(item.variant_id) !== vipId &&
        String(item.product_id) !== insuranceProductId && item.properties?._pawever_shipping_insurance !== 'true');
      if ((draft.lineKey && !oldItem) || hasOtherCard || !hasMainItem) {
        const error = new Error('Your cart has changed. Close this window to refresh it before editing your card.');
        error.reviewCart = true;
        throw error;
      }
      const properties = {
        ...(oldItem?.properties || draft.properties),
        _pawever_message_card: 'true',
        _pawever_card_design: draft.style + '-v1',
        _pawever_card_background: find('[data-pmc-preview-image]').src,
        Message: message.value,
        Signature: find('[data-pmc-signature]').value.trim()
      };
      const quantity = oldItem?.quantity || 1;
      mutationStarted = true;
      if (oldItem && String(oldItem.variant_id) === draft.variantId) {
        await request('change.js', { id: oldItem.key, quantity, properties });
      } else {
        // Keep the previous card until its replacement has actually been added.
        const added = await request('add.js', { items: [{ id: Number(draft.variantId), quantity, properties }] });
        if (oldItem) {
          try {
            await request('change.js', { id: oldItem.key, quantity: 0 });
          } catch (error) {
            // If removal fails, try restoring the original cart without losing its message.
            await request('change.js', { id: added.items[0].key, quantity: 0 });
            throw error;
          }
        }
      }
      try { sessionStorage.setItem('paweverGiftExpandedOnce', 'true'); } catch (_) {}
      window.location.reload();
    } catch (error) {
      saving = false;
      needsReview = mutationStarted || Boolean(error.reviewCart);
      editor.removeAttribute('aria-busy');
      editor.querySelectorAll('button, input, textarea').forEach((control) => { control.disabled = false; });
      find('[data-pmc-save]').disabled = needsReview;
      find('[data-pmc-save]').textContent = 'Save Message';
      const status = find('[data-pmc-error]');
      status.textContent = mutationStarted
        ? 'We could not confirm the cart update. Close this window to reload and review your cart before trying again.'
        : error.message || 'Unable to save your message. Please try again.';
      status.hidden = false;
    }
  }

  document.addEventListener('click', (event) => {
    const button = event.target.closest('[data-pawever-message-card], [data-pmc-edit]');
    if (button) { event.preventDefault(); openEditor(button); return; }
    if (event.target.closest('[data-pmc-cancel]')) closeEditor();
  });
  document.addEventListener('input', (event) => {
    if (event.target.matches('[data-pmc-message], [data-pmc-signature]')) updatePreview();
  });
  document.addEventListener('change', (event) => {
    if (!event.target.matches('[data-pmc-editor] input[name="pmc-preset"]')) return;
    find('[data-pmc-message]').value = event.target.value;
    updatePreview();
  });
  document.addEventListener('submit', (event) => {
    if (event.target.matches('[data-pmc-form]')) saveMessage(event);
  });
  document.addEventListener('cancel', (event) => {
    if (!event.target.matches('[data-pmc-editor]')) return;
    event.preventDefault();
    closeEditor();
  }, true);
  window.addEventListener('resize', updatePreview);
})();
